// dataLayer wrapper per GTM. Nomi eventi snake_case (convenzione GA4).
// Mai chiamare direttamente window.dataLayer.push nei componenti: usare track.*
// così abbiamo un solo punto in cui filtrare / debounce / strip-PII.

declare global {
  interface Window {
    dataLayer?: Array<Record<string, any>>;
  }
}

function push(payload: Record<string, any>) {
  if (typeof window === "undefined") return;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(payload);
}

// Reset dei custom param della hit precedente: GA4/GTM persistono i campi
// finche non vengono sovrascritti, e questo crea contaminazione tra eventi.
function resetThenPush(event: string, params: Record<string, any>) {
  const cleared: Record<string, any> = {};
  for (const k of Object.keys(params)) cleared[k] = undefined;
  push(cleared);
  push({ event, ...params });
}

export const track = {
  // SPA navigation: chiamata da AuthenticatedApp ad ogni cambio di view.
  pageView: (params: { page_path: string; page_title: string; view: string; team_id?: string | null }) =>
    resetThenPush("page_view", {
      page_path: params.page_path,
      page_title: params.page_title,
      page_location: typeof window !== "undefined" ? window.location.href : undefined,
      view: params.view,
      ...(params.team_id ? { team_id: params.team_id } : {}),
    }),

  // Click su una voce di navigazione principale (sidebar / sheet mobile / palette).
  navigation: (view: string, source: "sidebar" | "mobile_sheet" | "palette" | "shortcut" | "header" = "sidebar") =>
    resetThenPush("navigation", { view, source }),

  // Cambio tab interno a una view (es. amichevoli / stagione / playoff in Mondo FC).
  tabChange: (section: string, tab: string) => resetThenPush("tab_change", { section, tab }),

  // CTA: bottoni che aprono creazione / azioni primarie.
  ctaClick: (cta: string, extra: Record<string, any> = {}) =>
    resetThenPush("cta_click", { cta, ...extra }),

  // Selezione di un contenuto (player, ea match, ecc.). Compatibile con GA4 select_content.
  selectContent: (contentType: string, contentId: string, extra: Record<string, any> = {}) =>
    resetThenPush("select_content", { content_type: contentType, content_id: contentId, ...extra }),

  // Apertura dialog / modal importanti.
  dialogOpen: (dialog: string, extra: Record<string, any> = {}) =>
    resetThenPush("dialog_open", { dialog, ...extra }),

  // Switch dell active team (founder/super_admin con piu squadre).
  teamSwitch: (toTeamId: string) => resetThenPush("team_switch", { team_id: toTeamId }),

  // Login / logout — utili per legare il funnel.
  login: (method: "password" | "magic_link" | "oauth" = "password") =>
    resetThenPush("login", { method }),
  logout: () => resetThenPush("logout", {}),

  // Sign up: nome evento standard GA4 ("sign_up") con parametro `method`.
  // `source` distingue il canale di acquisizione (form pubblico,
  // invito diretto, magic link di risposta) per segmentare il funnel.
  signUp: (params: {
    source: "auth_screen" | "invite_link" | "response_page";
    method?: "password" | "magic_link" | "oauth";
  }) =>
    resetThenPush("sign_up", {
      method: params.method || "password",
      source: params.source,
    }),

  // Conversione attivazione founder: signup -> primo team creato.
  teamCreated: (params: { team_id?: string | null; has_ea_club?: boolean }) =>
    resetThenPush("team_created", {
      ...(params.team_id ? { team_id: params.team_id } : {}),
      has_ea_club: Boolean(params.has_ea_club),
    }),

  // Engagement: wizard sessione completato (training o spieltag).
  sessionCreated: (params: {
    team_id: string;
    session_id?: string | null;
    has_matches?: boolean;
    include_guests?: boolean;
  }) =>
    resetThenPush("session_created", {
      team_id: params.team_id,
      ...(params.session_id ? { session_id: params.session_id } : {}),
      has_matches: Boolean(params.has_matches),
      include_guests: Boolean(params.include_guests),
    }),

  // Azioni sul link di invito diretto al team.
  inviteLink: (
    action: "created" | "regenerated" | "copied" | "activated" | "deactivated",
    params: {
      team_id: string;
      expires_in_days?: number | null;
      max_uses?: number | null;
    },
  ) =>
    resetThenPush("invite_link", {
      action,
      team_id: params.team_id,
      ...(params.expires_in_days != null ? { expires_in_days: params.expires_in_days } : {}),
      ...(params.max_uses != null ? { max_uses: params.max_uses } : {}),
    }),

  // Risposta del player al magic link di una sessione (pubblico, anche senza account).
  // `is_match_day` distingue spieltag da training; `team_id` non sempre disponibile.
  sessionResponse: (params: {
    session_id: string;
    status: "present" | "late" | "absent";
    is_match_day?: boolean;
    team_id?: string | null;
  }) =>
    resetThenPush("session_response", {
      session_id: params.session_id,
      status: params.status,
      is_match_day: Boolean(params.is_match_day),
      ...(params.team_id ? { team_id: params.team_id } : {}),
    }),

  // Founder/coach preme "Invia convocazioni" e il batch push parte.
  // `sent` nuovi e `already` gia' notificati per capire incrementalita'.
  callupSent: (params: {
    team_id: string;
    session_id: string;
    sent: number;
    already: number;
  }) =>
    resetThenPush("callup_sent", {
      team_id: params.team_id,
      session_id: params.session_id,
      sent: params.sent,
      already: params.already,
    }),
};
